import { supabase } from '@/lib/supabase';

export interface StockHistoryEntry {
  id: string;
  product_id: string;
  product_name: string;
  category: string;
  change_type: 'deduct' | 'restore' | 'manual_update' | 'restock';
  change_amount: number;
  stock_before: number;
  stock_after: number;
  variant_label?: string;
  order_id?: string;
  order_status?: string;
  note?: string;
  performed_by: string;
  created_at: string;
}

export interface LogStockParams {
  product_id: string;
  product_name: string;
  category?: string;
  change_type: StockHistoryEntry['change_type'];
  change_amount: number;
  stock_before: number;
  stock_after: number;
  variant_label?: string;
  order_id?: string;
  order_status?: string;
  note?: string;
  performed_by?: string;
}

export async function logStockChange(params: LogStockParams): Promise<void> {
  try {
    await supabase.from('stock_history').insert({
      product_id: params.product_id,
      product_name: params.product_name,
      category: params.category ?? '',
      change_type: params.change_type,
      change_amount: params.change_amount,
      stock_before: params.stock_before,
      stock_after: params.stock_after,
      variant_label: params.variant_label ?? null,
      order_id: params.order_id ?? null,
      order_status: params.order_status ?? null,
      note: params.note ?? null,
      performed_by: params.performed_by ?? 'admin',
    });
  } catch (e) {
    console.error('[StockHistory] Log failed:', e);
  }
}

export async function fetchStockHistory(options?: {
  productId?: string;
  changeType?: string;
  limit?: number;
  offset?: number;
  from?: string;
  to?: string;
}): Promise<{ data: StockHistoryEntry[]; total: number }> {
  try {
    let query = supabase
      .from('stock_history')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false });

    if (options?.productId) query = query.eq('product_id', options.productId);
    if (options?.changeType && options.changeType !== 'all') query = query.eq('change_type', options.changeType);
    if (options?.from) query = query.gte('created_at', options.from);
    if (options?.to) query = query.lte('created_at', options.to);

    const limit = options?.limit ?? 50;
    const offset = options?.offset ?? 0;
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;
    if (error) throw error;
    return { data: (data ?? []) as StockHistoryEntry[], total: count ?? 0 };
  } catch (e) {
    console.error('[StockHistory] Fetch failed:', e);
    return { data: [], total: 0 };
  }
}

export async function fetchStockHistoryStats(): Promise<{
  totalDeductions: number;
  totalRestores: number;
  totalManual: number;
  totalRestocks: number;
  todayChanges: number;
}> {
  try {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const [allRes, todayRes] = await Promise.all([
      supabase.from('stock_history').select('change_type'),
      supabase.from('stock_history').select('id').gte('created_at', todayStart.toISOString()),
    ]);

    const all = (allRes.data ?? []) as { change_type: string }[];
    return {
      totalDeductions: all.filter(r => r.change_type === 'deduct').length,
      totalRestores: all.filter(r => r.change_type === 'restore').length,
      totalManual: all.filter(r => r.change_type === 'manual_update').length,
      totalRestocks: all.filter(r => r.change_type === 'restock').length,
      todayChanges: (todayRes.data ?? []).length,
    };
  } catch {
    return { totalDeductions: 0, totalRestores: 0, totalManual: 0, totalRestocks: 0, todayChanges: 0 };
  }
}
